/**
 * The KPI workflow API, and the only place the app talks to `/api/kpi`.
 *
 * Errors are thrown, not swallowed into `null`: unlike a read-only panel that
 * can degrade to an empty state, every call here is something the user asked
 * for by pressing a button, and "nothing happened, silently" is the one outcome
 * the composer must never produce. The page turns a thrown error into a visible
 * message.
 */

import {
	type CreateWorkflowPayload,
	type MetricGroup,
	type Workflow,
	type WorkflowRun,
	parseMetricCatalog,
	parseRun,
	parseRunList,
	parseWorkflow,
	parseWorkflowList,
} from "../types/kpiWorkflow";
import { apiFetch } from "../utils/apiClient";

const BASE = "/api/kpi";

/** FastAPI puts the human-readable reason in `detail`; fall back to the status. */
async function readError(response: Response, fallback: string): Promise<string> {
	try {
		const body = await response.json();
		const detail = body?.detail;
		if (typeof detail === "string" && detail.trim()) return detail;
		// 422s carry a list of field errors; the first one is the useful part.
		if (Array.isArray(detail) && typeof detail[0]?.msg === "string") {
			return detail[0].msg;
		}
	} catch {
		// Non-JSON body — the generic message is the best we have.
	}
	return `${fallback} (${response.status})`;
}

/**
 * The workflow (or run) this call named does not exist — for this user, which
 * the backend deliberately does not distinguish from "at all".
 *
 * Its own type because one caller must act on it rather than report it: the
 * run notifier polls a workflow's history, and a workflow deleted while it was
 * being watched (DOP-491) answers 404 on every poll forever. Any other failure
 * there is worth retrying; this one is a fact. It still extends `Error`, so
 * every caller that only shows `error.message` keeps working unchanged.
 */
export class WorkflowNotFoundError extends Error {
	constructor(message: string) {
		super(message);
		this.name = "WorkflowNotFoundError";
	}
}

async function request(
	path: string,
	init: RequestInit,
	fallback: string,
): Promise<unknown> {
	const response = await apiFetch(`${BASE}${path}`, init);
	if (!response.ok) {
		const message = await readError(response, fallback);
		if (response.status === 404) throw new WorkflowNotFoundError(message);
		throw new Error(message);
	}
	return response.json();
}

export async function listMetricGroups(): Promise<MetricGroup[]> {
	return parseMetricCatalog(
		await request(
			"/metric-definitions",
			{ method: "GET" },
			"Could not load the metric list",
		),
	);
}

export async function listWorkflows(): Promise<Workflow[]> {
	return parseWorkflowList(
		await request(
			"/workflows",
			{ method: "GET" },
			"Could not load your workflows",
		),
	);
}

export async function createWorkflow(
	payload: CreateWorkflowPayload,
): Promise<Workflow> {
	return parseWorkflow(
		await request(
			"/workflows",
			{
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(payload),
			},
			"Could not save this workflow",
		),
	);
}

/**
 * Delete a workflow and its run history.
 *
 * The values it already extracted stay on the dashboard — the backend keeps
 * them deliberately, because a number already read out of a file does not stop
 * being true when the instruction that asked for it is retired.
 *
 * Rejects with the backend's 409 while a run is in flight; the message names
 * what to do about it, so it is surfaced verbatim rather than replaced.
 */
export async function deleteWorkflow(workflowId: string): Promise<void> {
	const response = await apiFetch(
		`${BASE}/workflows/${encodeURIComponent(workflowId)}`,
		{ method: "DELETE" },
	);
	if (!response.ok) {
		throw new Error(await readError(response, "Could not delete this workflow"));
	}
}

/**
 * The backend refused to start a second run because one is already going.
 *
 * Not an error the user should be shown as a failure: they asked for an
 * extraction and one IS running. The run already in flight travels with the
 * rejection so the caller can watch that instead — see the one-active-run
 * index (DOP-269), which is what makes "already in progress" a fact rather
 * than a guess.
 */
export class ActiveRunConflictError extends Error {
	constructor(
		message: string,
		readonly activeRun: WorkflowRun | null,
	) {
		super(message);
		this.name = "ActiveRunConflictError";
	}
}

/**
 * Start a run. Resolves with the run row even when scheduling failed — the
 * backend records that as a `failed` run rather than dropping it, and the
 * caller is expected to read `status`.
 *
 * Throws :class:`ActiveRunConflictError` on 409, carrying the run that is
 * already in progress when the response names one.
 *
 * `force` re-reads content this client already has values from. It is sent
 * explicitly rather than omitted, so the request says which kind of run it is.
 */
export async function runWorkflow(
	workflowId: string,
	options: { force?: boolean } = {},
): Promise<WorkflowRun> {
	const response = await apiFetch(
		`${BASE}/workflows/${encodeURIComponent(workflowId)}/run`,
		{
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				trigger: "manual",
				force: options.force === true,
			}),
		},
	);
	if (response.status === 409) {
		// Read the body once, defensively: a 409 from anywhere but this endpoint
		// (a proxy, say) would not carry a run, and a missing one must degrade to
		// "something is running" rather than to a crash.
		let detail = "A run for this workflow is already in progress.";
		let activeRun: WorkflowRun | null = null;
		try {
			const body = await response.json();
			if (typeof body?.detail === "string" && body.detail.trim()) {
				detail = body.detail;
			}
			if (body?.active_run) activeRun = parseRun(body.active_run);
		} catch {
			// Keep the default message; the status alone is the useful part.
		}
		throw new ActiveRunConflictError(detail, activeRun);
	}
	if (!response.ok) {
		throw new Error(await readError(response, "Could not start this run"));
	}
	return parseRun(await response.json());
}

/**
 * Ask an in-flight run to stop.
 *
 * Rejects with the backend's 409 when the run has already finished — the caller
 * must not report a stop that did not happen.
 */
export async function cancelRun(
	workflowId: string,
	runId: string,
): Promise<WorkflowRun> {
	return parseRun(
		await request(
			`/workflows/${encodeURIComponent(workflowId)}/runs/${encodeURIComponent(
				runId,
			)}/cancel`,
			{ method: "POST" },
			"Could not cancel this run",
		),
	);
}

/**
 * Set the table to what it showed when this run finished.
 *
 * Resolves to the whole history, because restoring a version changes which run
 * is current and which others can be restored — every row is stale, not one.
 *
 * Rejects with the backend's 409 and its reason when it cannot be done: a run
 * is in progress, this is already the current version, or a newer run was
 * built over it.
 */
export async function restoreRunVersion(
	workflowId: string,
	runId: string,
): Promise<WorkflowRun[]> {
	return parseRunList(
		await request(
			`/workflows/${encodeURIComponent(workflowId)}/runs/${encodeURIComponent(
				runId,
			)}/restore`,
			{ method: "POST" },
			"Could not restore this version",
		),
	);
}

export async function listRuns(workflowId: string): Promise<WorkflowRun[]> {
	return parseRunList(
		await request(
			`/workflows/${encodeURIComponent(workflowId)}/runs`,
			{ method: "GET" },
			"Could not load the run history",
		),
	);
}
